import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useSearchParams } from "react-router-dom";
import { match } from "ts-pattern";
import { Box, Stack, Typography, Badge } from "@mui/material";
import { SearchProductFragment, useSearchLazyQuery, useSearchQuery } from "../../generated/storefront";
import { productSearchFilter } from "../../helpers/product";
import { Loader } from "../../components/Loader";
import { useAppState } from "../../state";
import useLoadMoreOnScroll from "../../hooks/useLoadMoreOnScroll";
import AutoResizeText from "../../components/AutoResizeText";
import Tabs from "../../components/Tabs";
import ArtistTab from "./ArtistTab";
import ProductTab from "./ProductTab";
import OtherTab from "./OtherTab";
import { FiltersButton } from "../Shop/styles";
import FrameBadge from "../../components/FrameBadges/FrameBadge";
import FrameBadges from "../../components/FrameBadges";
import { Frame, Medium, Size } from "../../types/product";
import { colors } from "../../theme";
import { Icon } from "../../components/Icon";
import BottomSheet from "../../components/BottomSheet";
import FiltersDrawer from "../Shop/Filters/Drawer";
import FiltersDesktopDrawer from "../Shop/Filters/DesktopDrawer";
import { firebaseClient } from "../../helpers/apolloClients";
import { useSearchArtistsQuery } from "../../generated/firebase";

const Search = () => {
  const ref = useRef<HTMLDivElement>(null);
  const { selectedCountry, isMobileScreen } = useAppState();
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<"products" | "artists" | "other">("products");
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);
  const [openFrameBar, setOpenFrameBar] = useState(false);
  const [selectedFrame, setSelectedFrame] = useState<Frame>(Frame.Natural);
  const searchQs = searchParams.get("qs") || "";
  const filters = ["colour", "month", "medium", "material", "size", "style", "subject", "orientation"];
  let filterCount = 0;
  searchParams.forEach((value, key) => filters.includes(key) && value && filterCount++);

  const month = searchParams.get("month");
  const medium = searchParams.get("medium");
  const size = searchParams.get("size");
  const style = searchParams.get("style");
  const subject = searchParams.get("subject");
  const orientation = searchParams.get("orientation");
  const colour = searchParams.get("colour");
  const material = searchParams.get("material");

  const mediums = (medium?.split(",") || []) as Medium[];
  const styles = style?.split(",") || [];
  const subjects = subject?.split(",") || [];
  const sizes = (size?.split(",") || []) as Size[];
  const orientations = orientation?.split(",") || [];
  const colours = colour?.split(",") || [];
  const months = month?.split(",") || [];
  const canvas = material === "canvas";

  const gmStyles = styles.map((style) => `gm.${style}`);
  const gmSubjects = subjects.map((subject) => `gm.${subject}`);
  const filterArray = [
    ...(colours.length ? [`${colours.join(" OR ")}`] : []),
    ...(styles.length ? [`tag:${gmStyles.join(" OR ")}`] : []),
    ...(subjects.length ? [`tag:${gmSubjects.join(" OR ")}`] : []),
  ];
  const filterQuery = filterArray.join(" ");
  const productFilters = [
    ...months.map((m) => ({ productType: m })),
    ...sizes.map((s) => ({ variantOption: { name: "size", value: s } })),
    ...orientations.map((o) => ({ productMetafield: { namespace: "custom", key: "orientation", value: o } })),
    ...mediums.map((m) => ({ productMetafield: { namespace: "custom", key: "medium", value: m } })),
    ...(canvas ? [{ productMetafield: { namespace: "custom", key: "available_for_canvas", value: "true" } }] : []),
  ];
  const { data: artistsData, loading: loadingArtists } = useSearchArtistsQuery({
    client: firebaseClient,
    variables: {
      limit: 24,
      query: searchQs,
    },
  });
  const artists = artistsData?.artists?.nodes || [];
  const {
    data: favouriteProductsData,
    loading: loadingFavouriteProducts,
    fetchMore: fetchMoreFavouriteProducts,
  } = useSearchQuery({
    variables: {
      query: searchQs + " " + filterQuery + " tag:favourite",
      limit: 24,
      country: selectedCountry,
      productFilters,
    },
    fetchPolicy: "network-only",
    skip: !searchQs,
  });
  const { data: artistProductsData } = useSearchQuery({
    variables: {
      query: filterQuery + ` vendor:${artists.map((a) => a.id).join(" OR ")}`,
      limit: 24,
      country: selectedCountry,
      productFilters,
    },
    skip: !searchQs || artists.length === 0,
    fetchPolicy: "network-only",
  });
  const [getOtherProducts, { data: otherProductsData, loading: loadingOtherProducts, fetchMore: fetchMoreOtherProducts }] =
    useSearchLazyQuery({
      variables: {
        query: searchQs + " " + filterQuery + " NOT tag:favourite",
        limit: 24,
        country: selectedCountry,
        productFilters,
      },
      fetchPolicy: "network-only",
    });
  const favouriteProductsHasMore = favouriteProductsData?.search.pageInfo.hasNextPage || false;
  const favouriteProductsAfterCursor = favouriteProductsData?.search.pageInfo.endCursor || "";

  const otherProductsHasMore = otherProductsData?.search.pageInfo.hasNextPage || false;
  const otherProductsAfterCursor = otherProductsData?.search.pageInfo.endCursor || "";

  const fetchMore = favouriteProductsHasMore ? fetchMoreFavouriteProducts : fetchMoreOtherProducts;
  const loadingProducts = loadingFavouriteProducts || loadingOtherProducts;
  const hasMore = favouriteProductsHasMore || otherProductsHasMore;
  const afterCursor = favouriteProductsHasMore ? favouriteProductsAfterCursor : otherProductsAfterCursor;

  const loadMore = async () => {
    await fetchMore({
      variables: {
        afterCursor,
      },
    });
  };

  const { loadingMore } = useLoadMoreOnScroll(
    ref,
    loadMore,
    favouriteProductsHasMore || (otherProductsData ? otherProductsHasMore : false),
    favouriteProductsHasMore ? loadingFavouriteProducts : loadingOtherProducts
  );

  const initialLoad = useCallback(async () => {
    setLoading(true);
    if (!favouriteProductsData) return;
    const favouriteProducts = favouriteProductsData.search.nodes;
    const artistProducts = artistProductsData?.search.nodes || [];
    const combinedProducts = [
      ...(favouriteProducts || []),
      ...(artistProducts.filter((p) => !favouriteProducts.includes(p)) || []),
    ];
    if (combinedProducts.length === 0) await getOtherProducts();
    setLoading(false);
  }, [favouriteProductsData, artistProductsData, getOtherProducts]);

  useEffect(() => {
    initialLoad();
  }, [initialLoad]);

  const products = useMemo(() => {
    if (!favouriteProductsData) return [];
    const favouriteProducts = (favouriteProductsData.search.nodes.filter(productSearchFilter) || []) as SearchProductFragment[];
    const artistProducts = (artistProductsData?.search.nodes.filter(productSearchFilter) || []) as SearchProductFragment[];
    const otherProducts = (otherProductsData?.search.nodes.filter(productSearchFilter) || []) as SearchProductFragment[];
    return [
      ...(favouriteProducts || []),
      ...(artistProducts.filter((p) => !favouriteProducts.includes(p)) || []),
      ...(otherProducts || []),
    ];
  }, [favouriteProductsData, artistProductsData, otherProductsData]);

  return (
    <>
      <Helmet>
        <title>{`Search results for ${searchQs} | GoodMood`}</title>
      </Helmet>

      <Stack gap={5} paddingX={{ xs: 2, md: 5 }} paddingY={2} ref={ref}>
        {loadingArtists && loading && !products?.length ? (
          <Loader />
        ) : (
          <>
            <Stack gap={2} width="100%" alignItems="center">
              <AutoResizeText text={`${searchQs}`} align="center" />
              <Tabs
                tabs={[
                  { label: "Products", value: "products" },
                  { label: "Artists", value: "artists" },
                  { label: "Other", value: "other" },
                ]}
                selectedTab={selected}
                onTabChange={(value) => setSelected(value as "products" | "artists" | "other")}
              />

              {selected === "products" && (
                <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                  {isMobileScreen ? (
                    <Badge badgeContent={filterCount} color="secondary">
                      <FiltersButton onClick={() => setShowFilters(!showFilters)}>
                        <Stack direction="row" alignItems="center" gap={1}>
                          <Icon icon="filters" fill={colors.black} size={20} />
                        </Stack>
                      </FiltersButton>
                    </Badge>
                  ) : (
                    <FiltersButton onClick={() => setShowFilters(!showFilters)}>
                      <Stack direction="row" alignItems="center" gap={1}>
                        <Typography fontSize={12}>Filters{filterCount > 0 ? ` (${filterCount})` : ""}</Typography>
                        <Icon icon="filters" fill={colors.black} size={20} />
                      </Stack>
                    </FiltersButton>
                  )}
                  {isMobileScreen ? (
                    <Stack
                      onClick={() => setOpenFrameBar(true)}
                      component="button"
                      position="relative"
                      bgcolor={colors.white}
                      border={`2px solid ${colors.cardGrey}`}
                      borderRadius={100}
                      padding={0.25}
                    >
                      <FrameBadge frame={selectedFrame} size={30} />
                    </Stack>
                  ) : (
                    <Stack direction="row" alignItems="center" justifyContent="flex-end" minWidth={255}>
                      <FrameBadges selectedFrame={selectedFrame} setSelectedFrame={setSelectedFrame} size="medium" />
                    </Stack>
                  )}
                </Stack>
              )}

              <Box width="100%" paddingY={2}>
                {match(selected)
                  .with("products", () => (
                    <ProductTab
                      products={products || []}
                      loading={loadingProducts}
                      loadingMore={loadingOtherProducts || loadingMore}
                      searchQs={searchQs}
                      hasMore={hasMore}
                      showOtherProductsButton={!favouriteProductsHasMore && !otherProductsData}
                      loadMore={() => fetchMore({ variables: { afterCursor } })}
                      loadOtherProducts={getOtherProducts}
                      isMobileScreen={isMobileScreen}
                      selectedFrame={selectedFrame}
                    />
                  ))
                  .with("artists", () => (
                    <ArtistTab
                      artists={artists}
                      loadingArtists={loadingArtists}
                      searchQs={searchQs}
                      isMobileScreen={isMobileScreen}
                    />
                  ))
                  .with("other", () => <OtherTab searchQs={searchQs} />)
                  .exhaustive()}
              </Box>
            </Stack>
          </>
        )}
      </Stack>

      {isMobileScreen ? (
        <FiltersDrawer
          isOpen={showFilters}
          onClose={() => setShowFilters(!showFilters)}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          selectedCountry={selectedCountry}
          persitParam="qs"
        />
      ) : (
        <FiltersDesktopDrawer
          isOpen={showFilters}
          onClose={() => setShowFilters(!showFilters)}
          filterCount={filterCount}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          selectedCountry={selectedCountry}
          persitParam="qs"
        />
      )}

      {isMobileScreen && (
        <BottomSheet open={openFrameBar} onClose={() => setOpenFrameBar(false)}>
          <Typography>Frame colour</Typography>
          <FrameBadges
            selectedFrame={selectedFrame}
            setSelectedFrame={(frame: Frame) => {
              setSelectedFrame(frame);
              setOpenFrameBar(false);
            }}
            size="large"
            showLabels
          />
        </BottomSheet>
      )}
    </>
  );
};

export default Search;
